/**
 * A person's draft of something saved with a version counter: a Memory file
 * (slice 1d spec §6.1) or a doc (slice 2d spec §10.3). The editor holds the
 * version the draft started from (`base`), the draft, the newer version a
 * save ran into (`conflict`), and a newer version that arrived live while
 * the draft had changes (`newer`). Pure, so each page tests it on its own.
 */

/** What the editor drafts from: anything with a version, its content read by `contentOf`. */
export type Versioned = { version: number };

export type Draft<F extends Versioned, D> = {
  base: F;
  draft: D;
  conflict: F | null;
  newer: F | null;
};

/**
 * The editor's moves for one kind of saved thing: `contentOf` reads what a
 * person edits from it (a file's text, a doc's title and body), and `same`
 * says whether two drafts are the same.
 */
export function draftKit<F extends Versioned, D>(
  contentOf: (saved: F) => D,
  same: (a: D, b: D) => boolean,
) {
  type E = Draft<F, D>;

  const editorFor = (saved: F): E => ({
    base: saved,
    draft: contentOf(saved),
    conflict: null,
    newer: null,
  });

  const isDirty = (e: E) => !same(e.draft, contentOf(e.base));

  const conflicted = (e: E, current: F): E => ({ ...e, conflict: current, newer: null });

  return {
    editorFor,
    isDirty,
    edit: (e: E, draft: D): E => ({ ...e, draft }),
    discard: (e: E): E => ({ ...e, draft: contentOf(e.base) }),
    /**
     * A copy arrives (a refetch after a hub event, or a save's reply). An
     * older or the same version changes nothing; a newer one replaces a clean
     * editor, and waits behind "Reload?" when there's a draft.
     */
    receive: (e: E, saved: F): E => {
      if (saved.version <= e.base.version) return e;
      // The version a refused save already shows: the person chooses.
      if (e.conflict && saved.version <= e.conflict.version) return e;
      // Clean, or the newer version is the draft (their own save, landed while away).
      if (!isDirty(e) || same(contentOf(saved), e.draft)) return editorFor(saved);
      return { ...e, newer: saved };
    },
    /** A save of `sent` came back as `saved`; typing that went on meanwhile stays a draft on it. */
    saved: (e: E, saved: F, sent: D): E =>
      same(e.draft, sent) ? editorFor(saved) : { ...e, base: saved, conflict: null, newer: null },
    /**
     * An undo begun from `from` came back as `saved`. A draft that moved since
     * is kept, and the undone version shows as a conflict to resolve.
     */
    undone: (e: E, saved: F, from: D): E =>
      same(e.draft, from) ? editorFor(saved) : conflicted(e, saved),
    /** "Reload?": take the newer version, dropping the draft. */
    reload: (e: E): E => (e.newer ? editorFor(e.newer) : e),
    /** A save came back `409 conflict` with the current version. */
    conflicted,
    /** "Keep mine": the draft now sits on the newer version, to save from it. */
    keepMine: (e: E): E =>
      e.conflict ? { ...e, base: e.conflict, conflict: null, newer: null } : e,
    /** "Use theirs": the newer version, the draft dropped. */
    takeTheirs: (e: E): E => (e.conflict ? editorFor(e.conflict) : e),
  };
}

/** The copy to keep: `saved` (a fetch, a save, a refusal), unless the cache already holds a newer one. */
export function fresher<T extends Versioned>(cached: T | undefined, saved: T): T {
  return cached && cached.version >= saved.version ? cached : saved;
}
